/**
 * StoriesView - Stories library with categories and TTS
 * Displays religious stories, fairy tales, and life wisdom
 */
import React, { useState, useEffect } from 'react';
import { BookOpen, Volume2, ChevronLeft, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';
import storiesData from '../../data/stories.json';
import useTTS from '../../hooks/useTTS';

export default function StoriesView({ senior, onBack }) {
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Use centralized TTS hook
  const { speak, stop, isSpeaking } = useTTS({
    language: senior?.language || 'de',
    deviceId: senior?.device_id || 'default'
  });

  useEffect(() => {
    loadStories();
  }, [selectedCategory, senior]);

  const loadStories = () => {
    let filtered = storiesData.stories;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(s => s.category === selectedCategory);
    }

    // Filter by religion if senior has religion set
    if (senior?.religion && selectedCategory === 'religious') {
      filtered = filtered.filter(s => !s.religion || s.religion === senior.religion);
    }

    setStories(filtered);
  };

  const readAloud = (text) => {
    if (!text) {
      toast.error('Kein Text zum Vorlesen');
      return;
    }
    speak(text);
  };

  const stopReading = () => {
    stop();
  };

  const getCategoryIcon = (category) => {
    const icons = {
      religious: '✨',
      fairy_tale: '🌿',
      life_wisdom: '🦉'
    };
    return icons[category] || '📚';
  };

  const getCategoryName = (category) => {
    const names = {
      religious: 'Religiöse Geschichten',
      fairy_tale: 'Märchen',
      life_wisdom: 'Lebensweisheiten'
    };
    return names[category] || category;
  };

  // Detail View
  if (selectedStory) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <button
              onClick={() => {
                stopReading();
                setSelectedStory(null);
              }}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-xl font-bold flex items-center gap-3"
            >
              <ChevronLeft className="w-6 h-6" />
              Zurück
            </button>
            
            <button
              onClick={() => {
                if (isSpeaking) {
                  stopReading();
                } else {
                  const fullText = `${selectedStory.title}. ${selectedStory.content}`;
                  readAloud(fullText);
                }
              }}
              className={`px-6 py-3 rounded-xl text-xl font-bold flex items-center gap-3 ${
                isSpeaking 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
            >
              <Volume2 className="w-6 h-6" />
              {isSpeaking ? 'Stop' : 'Vorlesen'}
            </button>
          </div>

          {/* Story Content */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 text-2xl">
              <span>{getCategoryIcon(selectedStory.category)}</span>
              <span className="text-amber-600 font-semibold">
                {getCategoryName(selectedStory.category)}
              </span>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 leading-tight">
              {selectedStory.title}
            </h1>
            
            <div className="prose prose-2xl max-w-none">
              {selectedStory.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-2xl text-gray-700 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </div>

            {selectedStory.moral && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
                <div className="text-lg text-amber-800 font-semibold mb-2">
                  Moral der Geschichte:
                </div>
                <div className="text-xl text-amber-900">
                  {selectedStory.moral}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header & Filter */}
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Geschichten</h1>
        
        {/* Category Filter */}
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-6 py-3 rounded-xl text-lg font-bold transition-all ${
              selectedCategory === 'all'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Alle Geschichten
          </button>
          <button
            onClick={() => setSelectedCategory('religious')}
            className={`px-6 py-3 rounded-xl text-lg font-bold transition-all ${
              selectedCategory === 'religious'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            ✨ Religiöse Geschichten
          </button>
          <button
            onClick={() => setSelectedCategory('fairy_tale')}
            className={`px-6 py-3 rounded-xl text-lg font-bold transition-all ${
              selectedCategory === 'fairy_tale'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            🌿 Märchen
          </button>
          <button
            onClick={() => setSelectedCategory('life_wisdom')}
            className={`px-6 py-3 rounded-xl text-lg font-bold transition-all ${
              selectedCategory === 'life_wisdom'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            🦉 Lebensweisheiten
          </button>
        </div>
      </div>

      {/* Stories Grid */}
      {stories.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-24 h-24 mx-auto mb-4 text-gray-400" />
          <p className="text-2xl text-gray-600">Keine Geschichten in dieser Kategorie</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {stories.map((story) => (
            <button
              key={story.id}
              onClick={() => setSelectedStory(story)}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-6 text-left group"
            >
              <div className="text-4xl mb-3">{getCategoryIcon(story.category)}</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-amber-600 transition-colors">
                {story.title}
              </h3>
              <p className="text-lg text-gray-600 line-clamp-3 mb-4">
                {story.content}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-amber-600 font-semibold">
                  {getCategoryName(story.category)}
                </span>
                <BookOpen className="w-6 h-6 text-gray-400 group-hover:text-amber-600 transition-colors" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
